import { observeEvent } from "~/analytics";
import { getClientAuthToken } from "~/utils/token";
import { type ExpandContext, parseTemplate } from "~/utils/url-template";
import { wafFetch } from "~/utils/waf-token";

// From: https://stackoverflow.com/questions/41102060/typescript-extending-error-class
export class ErrorWithCode extends Error {
  code?: string;
  constructor(message: string, code: string) {
    super(message); // 'Error' breaks prototype chain here
    this.code = code;
    Object.setPrototypeOf(this, new.target.prototype); // restore prototype chain
  }
}

type EmptyObject = Record<string, unknown>;

// The params slot of an endpoint whose path template has no variables.
type NoParams = Record<string, never>;

export class AppAPIRestClient {
  private baseURL: string;
  private appKey: string;

  constructor(baseURL: string, appKey: string) {
    this.baseURL = baseURL;
    this.appKey = appKey;
  }

  login = this.POST<NoParams, LoginPayload, LoginResult>("/login", "login");
  serverSideLogin = this.POST<NoParams, LoginPayload, LoginResult>("/login");

  linkLoginStart = this.POST<NoParams, LoginLinkPayload, LoginLinkResult>(
    "/login/link",
  );

  linkLoginCode = this.POST<NoParams, LoginCodePayload, LoginCodeResult>(
    "/login/code",
  );

  updateProfile = this.POST<
    NoParams,
    Partial<UpdateProfilePayload>,
    LoginResult
  >("/customers/me", "update_profile");

  signup = this.POST<NoParams, SignupPayload, SignupResult>(
    "/signup",
    "sign_up",
  );

  logout = this.DELETE<NoParams, EmptyObject>("/sessions/current");

  sendPasswordReset = this.POST<
    NoParams,
    SendPasswordResetPayload,
    EmptyObject
  >("/recovery/reset", "send_password_reset");

  resetPassword = this.POST<
    { code: string },
    ResetPasswordPayload,
    EmptyObject
  >("/recovery/reset/{code}");

  verifyEmail = this.POST<NoParams, VerifyEmailPayload, EmptyObject>(
    "/session/verify",
    "verify_email_jwt",
  );

  sendVerifyEmail = this.POST<NoParams, EmptyObject, EmptyObject>(
    "/session/verify/send",
    "send_verify_email",
  );

  sendSupportMessage = this.POST<
    NoParams,
    SendSupportMessagePayload,
    EmptyObject
  >("/comment", "send_support_message");

  associateFCMToken = this.POST<
    NoParams,
    AssociateFCMTokenPayload,
    EmptyObject
  >("/customers/me/fcmtoken");

  async sendMetrics(metrics: string) {
    await wafFetch(`${this.baseURL}/metrics`, {
      method: "POST",
      body: metrics,
      headers: {
        "Content-Type": "text/plain",
      },
    });
  }

  private async doRequest<P, R>(
    method: string,
    path: string,
    payload?: P,
  ): Promise<R> {
    const token = getClientAuthToken();

    const res = await wafFetch(this.baseURL + path, {
      method: method,
      body: JSON.stringify(payload),
      headers: {
        authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
        "Tidy-App-Key": this.appKey,
      },
    });

    if (!res.ok) {
      if (res.status === 401 && typeof window !== "undefined") {
        window.location.href = "/logout";
      }

      let errBody;
      try {
        errBody = await res.json();
      } catch (err) {
        console.error("error parsing error message", err);
        throw new Error("Unknown error");
      }

      if (errBody.ErrorMessage && errBody.ErrorCode) {
        throw new ErrorWithCode(errBody.ErrorMessage, errBody.ErrorCode);
      }

      throw new Error(errBody.message || errBody.ErrorMessage);
    }

    return res.json();
  }

  // private GET<Params, Result>(pathTemplate: string) {
  //   const t = parseTemplate(pathTemplate);

  //   return (params: Params): Promise<Result> =>
  //     this.doRequest("GET", t.expand(params));
  // }

  private DELETE<Params extends ExpandContext, Result>(pathTemplate: string) {
    const t = parseTemplate(pathTemplate);

    return (params: Params): Promise<Result> =>
      this.doRequest("DELETE", t.expand(params));
  }

  private POST<Params extends ExpandContext, Payload, Result>(
    pathTemplate: string,
    eventAction?: string,
  ) {
    const t = parseTemplate(pathTemplate);

    return async (params: Params, payload: Payload): Promise<Result> => {
      const result: Result = await this.doRequest(
        "POST",
        t.expand(params),
        payload,
      );

      if (eventAction) {
        observeEvent({ action: eventAction });
      }

      return result;
    };
  }
}

type LoginPayload = {
  LoginService: string;
  DeviceID: string;
  FirstName?: string;
  LastName?: string;
} & (
  | {
      EmailAddress: string;
      Password: string;
      Signature: string;
    }
  | {
      AppleIdToken: string;
    }
  | {
      GoogleToken: string;
    }
  | {
      Token: string;
    }
  | {
      Nonce: string;
      AuthCode: string;
    }
  | {
      FirstName: string;
      LastName: string;
      EmailAddress: string;
      PhoneNumber: string;
      AgreedToTerms: boolean;
    }
);

interface LoginResult {
  accessToken: string;
  missingRequiredFields: boolean;
}

interface LoginLinkPayload {
  emailAddress: string;
  next?: string;
  location?: string;
}

interface LoginLinkResult {
  success: boolean;
  nonce: string;
  expiresAt: string;
}

interface LoginCodePayload {
  token: string;
}

interface LoginCodeResult {
  code: string;
}

interface SignupPayload {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  agreeToTerms: boolean;
  agreeToLoyalty: boolean;
  phoneNumber: string;
  DeviceID: string;
}

interface UpdateProfilePayload {
  emailAddress: string;
  phoneNumber: string;
  firstName?: string;
  lastName?: string;
  agreeToLoyalty?: boolean;
  agreeToTerms?: boolean;
}

interface SignupResult {
  accessToken: string;
}

interface SendPasswordResetPayload {
  EmailAddress: string;
}

interface ResetPasswordPayload {
  Password: string;
}

interface VerifyEmailPayload {
  jwt: string;
}

interface SendSupportMessagePayload {
  text: string;
  supportType: string;
}

interface AssociateFCMTokenPayload {
  fcmToken: string;
}
